'use client';

import { useEffect } from 'react';

interface PortalGoogleAnalyticsProps {
  /** The GA4 Measurement ID (e.g. G-XXXXXXXXXX) for the government's analytics property. */
  gaMeasurementId: string;
}

const GA_SCRIPT_ID = 'portal-ga-gtag-js';
const GA_CONFIG_SCRIPT_ID = 'portal-ga-config';

/**
 * Injects the Google Analytics gtag.js script for a government's portal and removes it on unmount.
 *
 * This component manually manages the script lifecycle rather than using `next/script` because
 * Next.js script tags persist across client-side navigations. Manual injection ensures GA is
 * cleaned up when the user navigates away from `/portal/:code`, preventing data from leaking
 * into other governments' portals or non-portal pages.
 */
export function PortalGoogleAnalytics({ gaMeasurementId }: PortalGoogleAnalyticsProps) {
  useEffect(() => {
    injectGaScripts(gaMeasurementId);

    return () => removeGaScripts();
  }, [gaMeasurementId]);

  return null;
}

function injectGaScripts(gaMeasurementId: string) {
  if (document.getElementById(GA_SCRIPT_ID)) return;

  // Initialize dataLayer and gtag before the script loads
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', gaMeasurementId);

  const gtagScript = document.createElement('script');
  gtagScript.id = GA_SCRIPT_ID;
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`;
  gtagScript.async = true;
  document.head.appendChild(gtagScript);

  // Store the measurement ID on the config script element so we can target it on cleanup
  const configScript = document.createElement('script');
  configScript.id = GA_CONFIG_SCRIPT_ID;
  configScript.dataset.gaMeasurementId = gaMeasurementId;
  document.head.appendChild(configScript);
}

function removeGaScripts() {
  document.getElementById(GA_SCRIPT_ID)?.remove();
  document.getElementById(GA_CONFIG_SCRIPT_ID)?.remove();

  // Clean up globals so a subsequent portal visit reinitialises cleanly
  delete window.gtag;
  delete window.dataLayer;
}
